/**
 * Native mobile media actions (Tauri/Android-iOS shell only) — save / export /
 * copy of remote media via the native `tauri-plugin-cyborg-push` commands.
 *
 * The web `<a download>` / blob / `navigator.clipboard` paths are no-ops inside
 * the mobile WebView (no Downloads / Photos / Files integration on iOS
 * WKWebView, none in the Android System WebView), so the attachment Save / Copy
 * actions route through here. Native fetches the bytes (no CORS) and writes to
 * the OS. Each helper swallows throws into `false` so callers can show a single
 * failure toast.
 *
 * Split out of ./push.ts (#543) so FCM push registration and native-media stay
 * separate concerns. push.ts re-exports these for back-compat.
 */
import { ensureMediaToken, mediaSrc } from "$lib/media/asset-url.svelte.js";

/**
 * The ONE seam this module has to the native side (CYBORG-963).
 *
 * Every command below downloads the url it is handed, on the other side of the
 * Tauri boundary, so the url that crosses has to be the resolved one: with
 * asset reads requiring a credential, a stored url is a 401 in Swift exactly as it is in the
 * WebView. And resolving is only half of it — `mediaSrc` appends a pass that
 * ALREADY exists, so the pass has to be primed first or a phone that has not
 * painted an image yet sends nothing. Both live here, once, rather than in four
 * copies where the next command added can quietly skip one.
 *
 * No user-activation wrinkle to work around, unlike the web clipboard path: the
 * Photos permission prompt and the export sheet are presented natively, not by
 * the WebView, so waiting for a mint before invoking costs one round-trip on a
 * cold start and loses no gesture. `ensureMediaToken` is one shared in-flight
 * mint app-wide and resolves to null rather than rejecting, so a relay without
 * the flag hands across exactly the url it does today.
 *
 * Returns null on any failure (import, mint, invoke), which is the swallow the
 * callers' single failure toast is built on.
 */
async function invokeNativeMedia<T>(
  command: string,
  url: string,
  extra?: Record<string, unknown>,
): Promise<T | null> {
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await ensureMediaToken();
    return await invoke<T>(`plugin:cyborg-push|${command}`, { url: mediaSrc(url), ...extra });
  } catch (err) {
    console.warn(`[native-media] ${command} failed`, err);
    return null;
  }
}

/**
 * Save a remote image to the device gallery via the native plugin. Routes on
 * BOTH mobile platforms: iOS writes to the Photos camera roll (add-only auth);
 * Android writes to Pictures/Cyborg7 via MediaStore. Resolves true on success,
 * false otherwise.
 */
export async function saveImageToPhotos(url: string): Promise<boolean> {
  const res = await invokeNativeMedia<{ saved?: boolean }>("save_image_to_photos", url);
  return Boolean(res?.saved);
}

/**
 * Save a remote video to the iOS Photos library via the native plugin. Swift
 * downloads the bytes to a temp file (no CORS), asks for add-only Photos
 * permission, and writes it to the camera roll as a video asset, then cleans up
 * the temp file. Resolves true on success, false otherwise.
 */
export async function saveVideoToPhotos(url: string): Promise<boolean> {
  const res = await invokeNativeMedia<{ saved?: boolean }>("save_video_to_photos", url);
  return Boolean(res?.saved);
}

/**
 * Export an arbitrary file (PDF / doc / any non-media attachment) to a location
 * the user picks via the native iOS document-export / share sheet. Swift
 * downloads the bytes to a temp file under `filename`, presents
 * `UIDocumentPickerViewController(forExporting:)`, and resolves once the sheet
 * is presented. Resolves true on success, false otherwise.
 */
export async function saveFile(url: string, name: string): Promise<boolean> {
  const res = await invokeNativeMedia<{ saved?: boolean }>("save_file", url, { filename: name });
  return Boolean(res?.saved);
}

/**
 * Copy a remote image to the iOS system clipboard via the native plugin — the
 * iOS equivalent of the web "Copy image". Swift downloads the bytes (no CORS),
 * decodes to a UIImage, and sets `UIPasteboard.general.image`. Resolves true on
 * success, false otherwise. Named *Native to avoid colliding with the web
 * helper in `$lib/media/clipboard.ts`.
 */
export async function copyImageToClipboardNative(url: string): Promise<boolean> {
  const res = await invokeNativeMedia<{ copied?: boolean }>("copy_image_to_clipboard", url);
  return Boolean(res?.copied);
}
